import * as React from "react";
import DocumentWorkspaceViewer from "../../../../components/DocumentWorkspaceViewer/DocumentWorkspaceViewer";
import { useFetchDocument } from "../../../../hooks/queries";
import { cn } from "../../../../utils/helpers";
import {
    describeConsentDevice,
    formatConsentAuthenticationMethod,
    formatConsentTimestamp,
    getConsentMetadataString,
    getOrderedPageEvidence,
} from "../../helpers/reports.helper";
import {
    IConsentDocument,
    IConsentReport,
    IExpiringConsent,
} from "../../types/reports.types";

interface ConsentDocumentViewerProps {
    consent: IConsentReport | IExpiringConsent;
    document: IConsentDocument;
    documentIndex: number;
    documentCount: number;
    onPreviousDocument: () => void;
    onNextDocument: () => void;
}

const renderAuditRow = (label: string, value: React.ReactNode) => (
    <div className="border-b border-strokelight py-[10px] last:border-b-0">
        <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-textSec">
            {label}
        </p>
        <p className="break-words text-sm leading-5 text-text">{value}</p>
    </div>
);

export default function ConsentDocumentViewer({
    consent,
    document,
    documentIndex,
    documentCount,
    onPreviousDocument,
    onNextDocument,
}: ConsentDocumentViewerProps) {
    const { data, error, isLoading } = useFetchDocument(
        document.document_url,
        true
    );
    const submittedActivity = consent.activity_log.find(
        (activity) => activity.activity_type === "client_submitted"
    );
    const submittedEvidence = consent.evidences?.find(
        (evidence) => evidence.step === "client_submitted"
    );
    const authenticationMethod = getConsentMetadataString(
        submittedActivity?.metadata,
        "authentication_method"
    );
    const submittedIpAddress = getConsentMetadataString(
        submittedActivity?.metadata,
        "ip_address"
    );
    const submittedUserAgent = getConsentMetadataString(
        submittedActivity?.metadata,
        "user_agent"
    );
    const signerName =
        submittedActivity?.user_name ??
        `${consent.client.client_first_name} ${consent.client.client_last_name}`.trim();
    const signedAt =
        consent.client_submitted_at ??
        submittedEvidence?.timestamp ??
        submittedActivity?.timestamp ??
        consent.signed_date;
    const isGeneratedConsent = document.document_type === "generated_consent";
    const hasSigningEvidence =
        isGeneratedConsent && Boolean(submittedActivity || submittedEvidence);
    let documentTypeLabel = "Uploaded attachment";
    if (isGeneratedConsent) {
        documentTypeLabel = hasSigningEvidence
            ? "E-signed consent"
            : "Generated consent";
    }
    const documentDateLabel = isGeneratedConsent ? "Signed" : "Uploaded";
    const documentDate = isGeneratedConsent ? signedAt : document.uploaded_at;

    const handleDownload = () => {
        if (!data?.data) return;

        const binary = window.atob(data.data);
        const bytes = new Uint8Array(binary.length);
        for (let index = 0; index < binary.length; index += 1) {
            bytes[index] = binary.charCodeAt(index);
        }
        const blob = new Blob([bytes], {
            type: data.content_type ?? document.content_type,
        });
        const url = window.URL.createObjectURL(blob);
        const anchor = window.document.createElement("a");
        anchor.href = url;
        anchor.download = document.document_name;
        window.document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    };

    const renderAuditPanel = (pageNumber: number, pageCount: number) => {
        const orderedPageEvidence = getOrderedPageEvidence(
            consent.evidences,
            pageCount,
            consent.payment_card_added_at
        );
        const pageEvidence = document.page_audit?.find(
            (audit) => audit.page_number === pageNumber
        ) ??
            orderedPageEvidence[pageNumber - 1] ?? {
                timestamp: signedAt ?? "",
                ip_address: submittedIpAddress ?? "",
                user_agent: submittedUserAgent ?? "",
            };

        return (
            <>
                <h3 className="mb-2 text-sm font-semibold text-text">
                    Audit details
                </h3>
                <dl>
                    {renderAuditRow("Signed by", signerName)}
                    {renderAuditRow(
                        "Signed on",
                        formatConsentTimestamp(pageEvidence?.timestamp)
                    )}
                    {renderAuditRow(
                        "Accessed from",
                        formatConsentAuthenticationMethod(authenticationMethod)
                    )}
                    {renderAuditRow(
                        "Device IP address",
                        pageEvidence?.ip_address ?? "Not recorded"
                    )}
                    {renderAuditRow(
                        "Device type",
                        describeConsentDevice(pageEvidence?.user_agent)
                    )}
                </dl>
            </>
        );
    };

    return (
        <DocumentWorkspaceViewer
            documentKey={document.document_id}
            data={data?.data}
            contentType={data?.content_type ?? document.content_type}
            isLoading={isLoading}
            isError={Boolean(error)}
            title={document.document_name}
            subtitle={
                <>
                    {consent.client.client_first_name}{" "}
                    {consent.client.client_last_name} · {documentDateLabel}{" "}
                    {formatConsentTimestamp(documentDate)}
                </>
            }
            badge={
                <span
                    className={cn(
                        "shrink-0 rounded-full px-8 py-[3px]",
                        "text-[11px] font-semibold",
                        isGeneratedConsent
                            ? "bg-[#E8F7F1] text-[#087A55]"
                            : "bg-[#EEF2F6] text-textSec"
                    )}
                >
                    {documentTypeLabel}
                </span>
            }
            navigation={{
                current: documentIndex + 1,
                total: documentCount,
                onPrevious: onPreviousDocument,
                onNext: onNextDocument,
            }}
            onDownload={handleDownload}
            renderSidePanel={isGeneratedConsent ? renderAuditPanel : undefined}
        />
    );
}
